import { getYear } from 'date-fns';
import { merge } from 'lodash';
import { getBrandName } from '../../config';
import { CustomerType } from '../../resources/base/models/enums';

/**
 * This service is a bridge between the application and whatever analytics
 * software is used.
 *
 * By defining the REACT_APP_ANALYTICS environment variable we are able to
 * either export the real or mocked implementation, so that from the application
 * point of view nothing changes and it can continue calling the analytics logic
 * as per usuall.
 */

export const getBrowserResolutionBreakpointString = (width) => {
  if (width <= 480) return 'xs';
  // eslint-disable-next-line no-else-return
  else if (width > 480 && width <= 720) return 's';
  // eslint-disable-next-line no-else-return
  else if (width > 720 && width <= 960) return 'm';
  // eslint-disable-next-line no-else-return
  else if (width > 960 && width <= 1280) return 'l';
  // eslint-disable-next-line no-else-return
  else if (width > 1280 && width <= 1600) return 'xl';
  // eslint-disable-next-line no-else-return
  else return 'xxl';
};

interface AnalyticsObj {
  core?: {
    stagingEnvironment?: string | null;
    dataLayerVersion?: string | null;
    pageInfo?: {
      pageName?: string | null;
      intendedCustomerDeviceType?: string | null;
      version?: string | null;
      releaseDate?: string | null;
      language?: string | null;
      market?: string | null;
      publisher?: string | null;
    };
    category?: {
      primaryCategory?: string | null;
      secondaryCategory?: string | null;
      siteType?: string | null;
      maturityLevel?: string | null;
      contractType?: string | null;
      inventoryType?: string | null;
    };
    attributes?: {
      journeyType?: string | null;
      viewChange?: string | null;
      brand?: string | null;
    };
  };
  error?: {
    errorCode?: string | null;
    errorMessage?: string | null;
    errorCausingURL?: string | null;
  };
  product?: {
    category?: string | null;
    name?: string | null;
    productAddons?: (string | undefined)[];
    attributes?: {
      typeOfSale?: string | null;
      paymentFrequency?: string | null;
      currency?: string | null;
      recurringPayment?: number | null;
      duration?: number | null;
      durationUnit?: string | null;
      startDateOfContract?: string | null;
      paymentType?: string | null;
      yearlyMileage?: string | null;
      contractMileage?: string | null;
      excessMileage?: string | null;
      mileageUnit?: string | null;
      registrationDateOwner?: string | null;
      balloonPayment?: string | null;
      netLoanAmount?: string | null;
      grossLoanAmount?: string | null;
      downPaymentAmount?: string | null;
      nominalInterestRate?: string | null;
      nominalInterestAmount?: string | null;
      effectiveInterestRate?: string | null;
      contractAmount?: number | null;
    };
    vehicleModel?: {
      manufacturer?: string | null;
      name?: string | null;
      descriptionLong?: string | null;
      bodyType?: string | null;
      initialRegistrationDate?: string | null;
      currency?: string | null;
      category?: string | null;
      salesID?: string | null;
      basePrice_localCurrency?: string | null;
      manufacturerCode?: string | null;
      endPrice_localCurrency?: string | null;
      typeOfUse?: string | null;
      condition?: string | null;
      engine?: {
        fuelType?: string | null;
        emission?: string | null;
        transmission?: string | null;
        enginePower?: string | null;
      };
    }[];
  }[];
  dealerData?: {
    companyId?: string | null;
    companyName?: string | null;
    address?: {
      street?: string | null;
      zipCode?: string | null;
      city?: string | null;
    };
  };
  form?: {
    type?: string | null;
    name?: string | null;
    errorFields?: [string | null] | null;
  };
  design?: {
    browserResolutionBreakpoint?: string | null;
  };
  customerData?: {
    loginStatus?: boolean | null;
    loggedInUserGroup?: string | null;
    yearOfBirth?: string | null;
    gender?: string | null;
    ccResult?: string | null;
    address?: {
      zipCode?: string | null;
    };
  };
  dataPrivacyStatement?: {
    allowPostalAds?: string | null;
    allowPhoneAds?: string | null;
    allowElectronicAds?: string | null;
    allowMailAds?: string | null;
    allowElectronicInvoices?: string | null;
  };
  eventInfo?: {
    eventType?: string | null;
    eventAction?: string | null;
    eventTargetURL?: string | null;
    linkInformation?: string | null | object;
  }[];
  event?: string | null;
}

declare global {
  interface Window {
    adobeDataLayer: AnalyticsObj[];
    _satellite: {
      track: (name: string) => void;
    };
  }
}

window.adobeDataLayer = window.adobeDataLayer || [];

const removeEventInfo = (obj: AnalyticsObj): void => {
  // eslint-disable-next-line
  obj.eventInfo = [];
};

const mock = {
  default: {} as AnalyticsObj,
  isLive: false,
  productStorageKey: 'analyticsProduct',

  setDefaults: function setDefaults(obj) {
    this.default = merge(this.default, obj);
  },
  push: function push(obj: AnalyticsObj): void {
    if (obj.product) {
      this.setDefaults({ product: obj.product });
      // Keep the analytics product stored, after payment page gets refreshed and the data is lost
      sessionStorage.setItem(this.productStorageKey, JSON.stringify(obj.product));
    }
    // remove EventInfo
    const prevDefault = { ...this.default };
    removeEventInfo(prevDefault);

    this.default = merge({}, prevDefault, obj) as AnalyticsObj;

    window.adobeDataLayer.push(this.default);
  },

  getProductFromFormData: (formData) => {
    // eslint-disable-next-line no-console
    console.debug('getProductFromFormData', formData);
    const {
      productType,
      paymentType,
      numberMonthlyInstallments,
      contractDuration,
      contractGrossPriceOneTime,
      carModelDescription,
      carRegistrationDate,
      customerType,
      contractGrossPriceMonthly,
      carNew,
    } = formData;
    let condition;
    if (carNew === undefined) {
      condition = null;
    } else {
      condition = carNew ? 'new' : 'used';
    }
    return [
      {
        productAddons: [productType.value],
        attributes: {
          recurringPayment: contractGrossPriceMonthly ?? null,
          paymentFrequency: paymentType === 'ONE_OFF' ? 'single' : 'monthly',
          duration: paymentType === 'ONE_OFF' ? null : (numberMonthlyInstallments ?? contractDuration),
          durationUnit: paymentType === 'ONE_OFF' ? null : 'MONTH',
          contractAmount:
            contractGrossPriceOneTime ??
            contractGrossPriceMonthly * (numberMonthlyInstallments ?? contractDuration ?? 12),
        },
        vehicleModel: [
          {
            manufacturer: getBrandName(),
            name: carModelDescription ?? null,
            initialRegistrationDate: carRegistrationDate ?? null,
            typeOfUse: customerType ?? null,
            condition,
          },
        ],
      },
    ];
  },

  getCustomerFromFormData: (formData) => {
    const { customerType, customerBirthDate, customerPostCode } = formData;
    const yearOfBirth = getYear(customerBirthDate);
    return {
      yearOfBirth: Number.isNaN(yearOfBirth) ? null : yearOfBirth,
      loggedInUserGroup: customerType === CustomerType.LEGAL_ENTITY ? 'business' : 'private',
      address: {
        zipCode: customerPostCode,
      },
    };
  },

  viewChange: function viewChange(): void {
    window._satellite && window._satellite.track('viewChange'); // eslint-disable-line
  },

  page: function page(): void {
    if (!this.isLive) {
      // eslint-disable-next-line no-console
      console.debug('track page', window.adobeDataLayer);
    }
    window._satellite && window._satellite.track('page'); // eslint-disable-line
  },

  interaction: function interaction(): void {
    if (!this.isLive) {
      // eslint-disable-next-line no-console
      console.debug('track interaction', window.adobeDataLayer);
    }
    window._satellite && window._satellite.track('interaction'); // eslint-disable-line
  },
};

const real = {
  ...mock,
  isLive: true,
};

let implementation = mock; // eslint-disable-line

if (process.env.REACT_APP_ANALYTICS) {
  implementation = real;
}

export { implementation as Analytics };
